/**
 * Real explore (SIGHT-2217, looker-enterprise PR #521): `explore_bidstream`,
 * model `client_analytics`. Fields live on the underlying
 * `explore_bidstream_base` LookML view, hence that prefix on query keys.
 *
 * No bid activity means an empty result set, not a zero-count row (per data
 * engineering).
 *
 * No comparison measure exists, so the delta chips (SIGHT-2207) come from a
 * second query against the equal-length preceding window (see
 * Bidstream.findItems), diffed here. Win Rate's delta is an absolute
 * percentage-point difference; Total Wins/Avg Bid CPM use a relative
 * percent change (computed in SummaryMetricCards). Total Bid Requests gets
 * no delta. An empty prior-period query falls back "*PriorPeriod" fields to
 * the current value (0% delta) - "where available" per the AC.
 *
 * `cpm` comes from a daily-grain spend table, so avgBidCpm is a date-cast
 * approximation for short windows like "Last 6 hours" - a documented
 * limitation of the explore itself.
 *
 * `win`/`win_rate` ("Validation Win"/"Validation Win Rate") mean
 * *bid-evaluation* pass/fail (matches Rejection Reasons' failed_* breakdown),
 * not delivered impressions. The summary cards and the Win/Loss trend below
 * use `win_impressions`/`lost_impressions` instead (sourced from the same
 * spend table as `cpm`), since those are the actual-impressions numbers meant
 * to be shown.
 *
 * The "last known activity" timestamp for the empty state was cut by PM -
 * it would need a separate, unbounded query.
 */
import { DateTime } from "luxon";
/**
 * `prior` is the same query re-run for the equal-length preceding window.
 * Both are scoped to one lineItemId, so `prior[0]` always matches
 * `current`'s row.
 */
export const parseBidstreamResponse = (current, prior) => current.map((entry) => {
    const priorEntry = prior[0];
    return {
        lineItemId: entry["explore_bidstream_base.lineitem_id"],
        lastUpdated: new Date(entry["explore_bidstream_base.last_updated"]),
        totalBidRequests: entry["explore_bidstream_base.ad_requests"],
        totalWins: entry["explore_bidstream_base.win_impressions"],
        totalWinsPriorPeriod: priorEntry?.["explore_bidstream_base.win_impressions"] ??
            entry["explore_bidstream_base.win_impressions"],
        winRate: entry["explore_bidstream_base.win_impressions_rate"],
        winRatePriorPeriod: priorEntry?.["explore_bidstream_base.win_impressions_rate"] ??
            entry["explore_bidstream_base.win_impressions_rate"],
        avgBidCpm: entry["explore_bidstream_base.cpm"],
        avgBidCpmPriorPeriod: priorEntry?.["explore_bidstream_base.cpm"] ?? entry["explore_bidstream_base.cpm"]
    };
});
/* ============================================================================
 * Win/Loss trend (SIGHT-2198)
 * ========================================================================= */
/**
 * The explore renders its time dimensions in New York wall-clock
 * (`FORMAT_TIMESTAMP(..., 'America/New_York')` in
 * bidstream_bid_validation_metrics.view.lkml), so buckets are generated in the
 * same zone rather than the viewer's - otherwise the gap-fill would not line up
 * with the buckets the warehouse actually returns.
 */
export const BIDSTREAM_TIME_ZONE = "America/New_York";
const toBucketKey = (moment, granularity) => granularity === "hour" ? moment.toFormat("yyyy-MM-dd'T'HH:00") : moment.toFormat("yyyy-MM-dd");
/**
 * Every bucket the window should contain, oldest first. Looker omits buckets
 * with no rows, so this is what the parse gap-fills against - without it a quiet
 * hour is drawn as a straight line between its neighbours instead of a dip.
 */
export const buildTrendBucketKeys = (granularity, windowHours, now = new Date()) => {
    const unit = granularity === "hour" ? "hours" : "days";
    const bucketCount = granularity === "hour" ? Math.max(1, windowHours) : Math.max(1, Math.ceil(windowHours / 24));
    // The current partial bucket counts as one - "last 6 hours" means this hour
    // plus the five before it.
    const end = DateTime.fromJSDate(now)
        .setZone(BIDSTREAM_TIME_ZONE)
        .startOf(granularity === "hour" ? "hour" : "day");
    return Array.from({ length: bucketCount }, (_unused, index) => toBucketKey(end.minus({ [unit]: bucketCount - 1 - index }), granularity));
};
const rowBucketKey = (row, granularity) => {
    // Looker date dimensions can come back with a time component appended;
    // only the date part participates in the key.
    const date = String(row["explore_bidstream_base.evaluation_date"] ?? "").slice(0, 10);
    if (granularity === "day") {
        return date;
    }
    const hour = String(row["explore_bidstream_base.evaluation_hour_of_day24"] ?? "").padStart(2, "0");
    return `${date}T${hour}:00`;
};
/**
 * Gap-fills every bucket in `bucketKeys` with zeros; rows outside that set
 * are dropped rather than appended, so a boundary row can't stretch the
 * axis. An empty response (no bid activity) stays empty rather than being
 * gap-filled, so the caller can show an empty state instead of a flat zero
 * line. Both measures are clamped at zero - the current hour can briefly
 * report a negative `lost_impressions` from pipeline lag.
 */
export const parseBidstreamTrendResponse = (rows, granularity, bucketKeys) => {
    if (rows.length === 0) {
        return [];
    }
    const byBucket = new Map();
    rows.forEach((row) => {
        const timestamp = rowBucketKey(row, granularity);
        const existing = byBucket.get(timestamp);
        const wins = Math.max(0, Number(row["explore_bidstream_base.win_impressions"]) || 0);
        const losses = Math.max(0, Number(row["explore_bidstream_base.lost_impressions"]) || 0);
        // Defensive: one row per bucket is expected, but a query that ends up
        // grouped by an extra dimension would otherwise drop all but the last.
        byBucket.set(timestamp, existing
            ? { timestamp, wins: existing.wins + wins, losses: existing.losses + losses }
            : { timestamp, wins, losses });
    });
    return bucketKeys.map((timestamp) => byBucket.get(timestamp) ?? { timestamp, wins: 0, losses: 0 });
};
//# sourceMappingURL=index.js.map